import React, { useEffect, useState } from 'react';

// ============================================================
// PromoModal — spontaneous attention-grabbing popup for the
// homepage, built on the same teal / gold / cream design system
// as the rest of the site (Playfair Display + DM Sans).
//
// Shows itself automatically after a short delay, on every page
// load (no persistence). Dismiss via the close icon, the
// "Maybe later" link, clicking the backdrop, or pressing Escape.
// ============================================================

const SHOW_AFTER_MS = 1400;

const FEATURES = [
  {
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    text: 'Fast-track processing',
  },
  {
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 12l2 2 4-4" /><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    text: '98% approval rate',
  },
  {
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2.5 1.8V22l4-1 4 1v-1.2L13 19v-5.5z" />
      </svg>
    ),
    text: '40+ destinations covered',
  },
];

export default function PromoModal() {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 260);
  };

  if (!visible) return null;

  return (
    <>
      <style>{`
        .pm-overlay {
          position: fixed; inset: 0; z-index: 2000;
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          background: rgba(20, 38, 46, 0.55);
          backdrop-filter: blur(5px);
          animation: pm-veil-in .32s ease-out both;
        }
        .pm-overlay.closing { animation: pm-veil-out .26s ease-in both; }
        @keyframes pm-veil-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pm-veil-out { from { opacity: 1; } to { opacity: 0; } }

        .pm-card {
          width: 100%; max-width: 460px;
          background: #fff;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 40px 80px -20px rgba(20,38,46,.5), 0 0 0 1px rgba(211,190,118,.15);
          position: relative;
          animation: pm-card-in .38s cubic-bezier(.22,.68,0,1.15) both;
        }
        .pm-overlay.closing .pm-card { animation: pm-card-out .24s ease-in both; }
        @keyframes pm-card-in { from { opacity: 0; transform: translateY(18px) scale(.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes pm-card-out { from { opacity: 1; transform: translateY(0) scale(1); } to { opacity: 0; transform: translateY(10px) scale(.97); } }
        @media (prefers-reduced-motion: reduce) {
          .pm-overlay, .pm-card, .pm-overlay.closing, .pm-overlay.closing .pm-card { animation: none !important; }
        }

        .pm-media { position: relative; height: 168px; overflow: hidden; }
        .pm-media img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .pm-media-veil {
          position: absolute; inset: 0;
          background: linear-gradient(180deg, rgba(26,49,61,.15) 0%, rgba(26,49,61,.85) 100%);
        }
        .pm-close {
          position: absolute; top: 14px; right: 14px; z-index: 3;
          width: 32px; height: 32px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,.16); backdrop-filter: blur(3px);
          border: 1px solid rgba(255,255,255,.35);
          color: #fff; cursor: pointer; transition: background .18s, transform .18s;
        }
        .pm-close:hover { background: rgba(255,255,255,.3); transform: rotate(90deg); }

        .pm-badge {
          position: absolute; top: 14px; left: 14px; z-index: 3;
          display: inline-flex; align-items: center; gap: 7px;
          background: #d3be76; color: #1a3140;
          font-family: 'DM Sans', sans-serif;
          font-size: 10px; font-weight: 700; letter-spacing: .8px; text-transform: uppercase;
          padding: 6px 12px; border-radius: 999px;
        }
        .pm-badge-dot { width: 6px; height: 6px; border-radius: 50%; background: #1a3140; animation: pm-pulse 1.6s ease-in-out infinite; }
        @keyframes pm-pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }

        .pm-icon-ring {
          position: absolute; bottom: -26px; left: 28px; z-index: 3;
          width: 52px; height: 52px; border-radius: 50%;
          background: #fff; border: 1px solid rgba(41,75,90,.1);
          box-shadow: 0 10px 24px rgba(26,49,61,.18);
          display: flex; align-items: center; justify-content: center;
          color: #294b5a;
        }

        .pm-body { padding: 40px 28px 26px; font-family: 'DM Sans', sans-serif; }
        .pm-eyebrow {
          font-size: 10.5px; font-weight: 700; letter-spacing: 1.4px; text-transform: uppercase;
          color: #b09a50; display: flex; align-items: center; gap: 9px; margin-bottom: 10px;
        }
        .pm-eyebrow::before { content: ''; width: 18px; height: 2px; background: #d3be76; border-radius: 2px; }
        .pm-title {
          font-family: 'Playfair Display', serif; font-weight: 600; color: #1a2f38;
          font-size: 23px; line-height: 1.24; margin: 0 0 10px;
        }
        .pm-title em { font-style: italic; color: #3b6272; }
        .pm-text { font-size: 13.5px; color: #6c7a80; font-weight: 300; line-height: 1.7; margin: 0 0 20px; }

        .pm-features { display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px; }
        .pm-feature { display: flex; align-items: center; gap: 10px; font-size: 12.5px; color: #294b5a; font-weight: 500; }
        .pm-feature-ico {
          width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
          background: rgba(211,190,118,.15); color: #b09a50;
          display: flex; align-items: center; justify-content: center;
        }

        .pm-actions { display: flex; flex-direction: column; gap: 10px; }
        .pm-cta {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          background: #d3be76; color: #1a3140; text-decoration: none;
          font-size: 13px; font-weight: 700; letter-spacing: .3px;
          border-radius: 8px; padding: 14px 20px;
          transition: background .18s, transform .18s;
          box-shadow: 0 10px 24px rgba(211,190,118,.3);
        }
        .pm-cta:hover { background: #e2ce86; transform: translateY(-1px); color: #1a3140; }
        .pm-skip {
          background: none; border: none; cursor: pointer;
          font-size: 12px; font-weight: 600; color: #8a9aa3;
          text-align: center; padding: 4px; transition: color .15s;
        }
        .pm-skip:hover { color: #294b5a; }
      `}</style>

      <div
        className={`pm-overlay${closing ? ' closing' : ''}`}
        onMouseDown={(e) => { if (e.target === e.currentTarget) dismiss(); }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-title"
      >
        <div className="pm-card">
          <div className="pm-media">
            <img
              src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=900&q=80&fit=crop&crop=center"
              alt=""
            />
            <div className="pm-media-veil" />
            <span className="pm-badge">
              <span className="pm-badge-dot" />
              Limited-Time Offer
            </span>
            <button className="pm-close" onClick={dismiss} aria-label="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" /></svg>
            </button>
            <div className="pm-icon-ring">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="3" width="16" height="18" rx="2" />
                <circle cx="12" cy="10" r="2.6" />
                <path d="M8.5 16.5c0-1.9 1.6-3 3.5-3s3.5 1.1 3.5 3" />
              </svg>
            </div>
          </div>

          <div className="pm-body">
            <div className="pm-eyebrow">Travel Documentation</div>
            <h3 id="pm-title" className="pm-title">
              Get <em>Visa-Ready</em> Without the Paperwork Stress
            </h3>
            <p className="pm-text">
              Our specialists handle the forms, the checklist and the follow-up —
              you just upload your documents and track your status in real time.
            </p>

            <div className="pm-features">
              {FEATURES.map((f, i) => (
                <div className="pm-feature" key={i}>
                  <span className="pm-feature-ico">{f.icon}</span>
                  {f.text}
                </div>
              ))}
            </div>

            <div className="pm-actions">
              <a href="/search-visa" className="pm-cta" onClick={dismiss}>
                View Visa Plans
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </a>
              <button className="pm-skip" onClick={dismiss}>
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}